import * as amplitude from '@amplitude/unified';
import { useEffect, useMemo, useRef } from 'react';

import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';

import { AnalyticsIdentity, buildAnalyticsIdentity } from './analytics-identity';
import { trackApplicationOpenedOnce } from './track-application-opened';

/**
 * Идентифицирует пользователя и его организацию в Amplitude. Вызывается один раз — в шелле
 * (`modules/app-shell/ui/layout/main-layout.tsx`).
 *
 * Почему хук, а не thunk `authCheck`: идентичность собирается из двух слайсов (`auth` и
 * `organizer`), которые наполняются независимо, и восстанавливается при F5 без повторного
 * входа. Плюс вызовы SDK — сайд-эффект UI, в `store/` им нельзя (`.claude/rules/architecture.md`).
 *
 * Сброс — в cleanup: при выходе шелл размонтируется (`modules/navigation/router.tsx`), и
 * `amplitude.reset()` отвязывает `device_id` от ушедшего пользователя.
 */
export function useAnalyticsIdentity(): void {
  const { user, userTournamentId } = useAuth();
  const { info } = useOrganizer();

  const identity = useMemo(() => buildAnalyticsIdentity(user, info, userTournamentId), [user, info, userTournamentId]);

  // Сигнатура, а не сам объект: `buildAnalyticsIdentity` возвращает новый объект на каждый
  // рендер, и эффект по ссылке слал бы `identify` при любом обновлении соседнего слайса.
  const signature = identity ? JSON.stringify(identity) : null;
  const sentSignature = useRef<string | null>(null);

  useEffect(() => {
    if (!identity || !signature || sentSignature.current === signature) {
      return;
    }

    sentSignature.current = signature;
    applyIdentity(identity);
    // После `setUserId`, чтобы событие уехало уже с пользователем.
    trackApplicationOpenedOnce(identity.userId);
  }, [identity, signature]);

  useEffect(
    () => () => {
      sentSignature.current = null;
      amplitude.reset();
    },
    [],
  );
}

function applyIdentity(identity: AnalyticsIdentity): void {
  amplitude.setUserId(identity.userId);

  const identifyEvent = new amplitude.Identify();
  Object.entries(identity.userProperties).forEach(([key, value]) => {
    identifyEvent.set(key, value);
  });
  amplitude.identify(identifyEvent);

  if (identity.organizerId !== null) {
    // Группа в Amplitude — всегда строка: по ней строится разбивка отчётов по организациям.
    amplitude.setGroup('organizer', String(identity.organizerId));
  }
}
